import React, { useRef, useEffect, useState } from "react";
import moment from "moment";
import { useTimezone } from "../../Context/TimezoneContext";
import domToImage from "dom-to-image";
import { Button, Card, Typography, makeStyles } from "@material-ui/core";
import PrintIcon from "@material-ui/icons/Print";
import { useEffectiveProfile } from "../../utils/useEffectiveProfile";
import { getCurrencySymbol, getTaxLabel } from "../../utils/currency";
import { computeSalesTotals } from "../../utils/salesReportTotals";
import SalesReportSections from "./SalesReportSections";

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(1.25),
    maxWidth: "800px",
    margin: "0 auto",
  },
  headerBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(0.75),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  titleText: {
    fontSize: "1rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  printButton: {
    textTransform: "none",
    padding: theme.spacing(0.5, 1.25),
    fontSize: "0.8125rem",
  },
  reportWrapper: {
    width: "100%",
    fontFamily: "Arial, sans-serif",
    "& @media print": {
      width: "80mm",
      margin: "0 5mm",
    },
  },
  headerText: {
    fontSize: "0.9375rem",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: theme.spacing(0.5),
  },
  divider: {
    borderBottom: "2px dotted #000",
    margin: "8px 0",
  },
  dataText: {
    fontSize: "0.8125rem",
    lineHeight: 1.4,
    "& > div": {
      marginBottom: "2px",
    },
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: theme.spacing(1),
    "& th": {
      fontSize: "0.8125rem",
      padding: "4px 0",
      borderBottom: "1px solid #ddd",
    },
    "& td": {
      fontSize: "0.75rem",
      padding: "4px 0",
    },
  },
  summaryText: {
    fontSize: "0.8125rem",
    "& > div": {
      margin: "4px 0",
    },
  },
  flexBetween: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  sectionTitle: {
    fontSize: "0.875rem",
    fontWeight: "bold",
    margin: "10px 0 4px 0",
  },
  footer: {
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "0.75rem",
    marginTop: theme.spacing(1),
  },
  // Add hidden styles for the print content
  //   hiddenPrintContent: {
  //     position: "fixed",
  //     left: "-9999px",
  //     top: 0,
  //     width: "556px", // Same width as in InvoiceTemplate
  //     padding: "20px 10px",
  //     lineHeight: "35px",
  //     backgroundColor: "white",
  //     border: "0px solid #FFFFFF",
  //   },
}));

const ThermalSalesReportIMIN = ({
  userProfile,
  salesData,
  selectedDate,
  reportType: reportTypeProp,
  selectedEmployeeName,
  users,
  selectedUserName,
  dayExtras,
}) => {
  const reportType = reportTypeProp || "consolidated";
  const classes = useStyles();
  const { formatInTimezone } = useTimezone();
  const profile = useEffectiveProfile();
  const currencySymbol = getCurrencySymbol(profile?.currency);
  const taxLabel = getTaxLabel(profile?.currency);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const printRef = useRef();

  useEffect(() => {
    const filtered = (salesData.invoices || []).filter(
      (invoice) =>
        moment(invoice.createdAt).format("YYYY-MM-DD") ===
        moment(selectedDate).format("YYYY-MM-DD")
    );
    setFilteredInvoices(filtered);
  }, [selectedDate, salesData.invoices]);

  // "employee-wise" groups by the staff member on shift; "user-wise" groups by
  // the login that rang the sale (always recorded as `creator`, so it works
  // even when shift tracking is switched off).
  const isBreakdown =
    reportType === "employee-wise" || reportType === "user-wise";

  const resolveUserName = (inv) => {
    const id = Array.isArray(inv.creator) ? inv.creator[0] : inv.creator;
    const match = (users || []).find((u) => u._id === id);
    return match?.name || match?.email || "Unknown user";
  };

  const groupKeyFor = (inv) =>
    reportType === "user-wise"
      ? resolveUserName(inv)
      : inv.employeeName || inv.employeeId || "Unassigned";

  const byEmployee = isBreakdown
    ? filteredInvoices.reduce((acc, inv) => {
        const key = groupKeyFor(inv);
        if (!acc[key]) acc[key] = [];
        acc[key].push(inv);
        return acc;
      }, {})
    : null;

  const employeeSummary =
    isBreakdown && byEmployee
      ? Object.entries(byEmployee)
          .map(([empName, invs]) => ({
            employeeName: empName,
            orderCount: invs.length,
            totalCollection: invs.reduce((s, i) => s + (i.total || 0), 0),
          }))
          .sort((a, b) => b.totalCollection - a.totalCollection)
      : [];

  const breakdownLabel = reportType === "user-wise" ? "User" : "Employee";

  // Calculate category-wise totals
  // const categoryTotals = filteredInvoices.reduce((acc, invoice) => {
  //   invoice.items.forEach((item) => {
  //     const category = item?.item?.category?.categoryName || "Uncategorized";
  //     console.log(category)
  //     acc[category] =
  //       (acc[category] || 0) + item.quantity * item?.item?.units?.sale?.price;
  //   });
  //   return acc;
  // }, {});

  // A sales report must reflect what was actually charged, so the rate is read
  // only from the invoice line (`rate`, recorded at sale time). Never fall back
  // to the item master price — that changes later via edits/Future Price Lists
  // and would silently restate historical sales.
  const getItemRate = (item) => {
    const raw = item?.rate ?? item?.overridePrice ?? item?.originalRate ?? 0;
    const parsed = parseFloat(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const getItemQty = (item) => {
    const raw = item?.quantity ?? item?.qty ?? 0;
    const parsed = parseFloat(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  // Totals come only from what each invoice stored (line rate, saved discount /
  // VAT / round-off / total) - see utils/salesReportTotals.js. Every invoice in
  // range counts.
  const salesTotals = computeSalesTotals(filteredInvoices);
  const {
    categoryTotals,
    paymentTotals,
    totalSales,
    totalDiscount,
    vatAdded,
    vatIncluded,
    roundOffTotal,
    netSales,
  } = salesTotals;

  const handlePrintWithImin = () => {
    // Check if IMIN printer is available
    if (window.IminPrinter) {
      var IminPrintInstance = new window.IminPrinter();
      console.log("IminPrinter", IminPrintInstance);

      try {
        // Create a WebSocket connection
        IminPrintInstance.connect().then(async (isConnect) => {
          // The WebSocket status is linked
          if (isConnect) {
            IminPrintInstance.initPrinter();
            const status = await IminPrintInstance.getPrinterStatus();
            console.log("Printer status:", status);

            const node = printRef.current;

            if (node) {
              domToImage.toJpeg(node).then(function (dataUrl) {
                IminPrintInstance.printSingleBitmap(dataUrl);
                setTimeout(function () {
                  // After the delay, feed paper
                  IminPrintInstance.printAndFeedPaper(100);
                  // setTimeout(function(){
                  //   alert("Function is being called! ThermalSalesReportIMIN");
                  //   IminPrintInstance.opencashBox();
                  // }, 1000);
                  // Cut the paper after feeding
                  IminPrintInstance.partialCut();
                }, 3000);
              });
            }
          }
        });
      } catch (err) {
        alert("Error: IMIN ERROR: " + err.message);
      }
    } else {
      alert("IminPrinter is not available.");
    }
  };

  const reportTitle =
    reportType === "user-wise"
      ? selectedUserName
        ? `Daily Sales Report – ${selectedUserName}`
        : "Daily Sales Report (User-wise)"
      : reportType === "employee-wise"
      ? selectedEmployeeName
        ? `Daily Sales Report – ${selectedEmployeeName}`
        : "Daily Sales Report (Employee-wise)"
      : "Daily Sales Report";

  return (
    <Card className={classes.card}>
      <div className={classes.headerBar}>
        <Typography className={classes.titleText}>{reportTitle}</Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<PrintIcon fontSize="small" />}
          onClick={handlePrintWithImin}
          className={classes.printButton}
        >
          Print Report
        </Button>
      </div>

      {/* Visible preview */}
      <div id="thermal-sales-report" className={classes.reportWrapper}>
        <div className={classes.headerText}>
          {reportTitle}
        </div>
        <div className={classes.headerText}>{userProfile.businessName}</div>

        <div className={classes.divider} />

        <div className={classes.dataText}>
          <div>Date: {formatInTimezone(selectedDate, "MMM DD, YYYY")}</div>
          <div>Printed On: {formatInTimezone(new Date(), "MMM DD, YYYY HH:mm:ss")}</div>
          <div>Total Orders: {filteredInvoices.length}</div>
        </div>

        {isBreakdown && employeeSummary.length > 0 && (
          <>
            <div className={classes.divider} />
            <div className={classes.sectionTitle}>{breakdownLabel}-wise collection</div>
            <table className={classes.table} style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>{breakdownLabel}</th>
                  <th style={{ textAlign: "right" }}>Invoices</th>
                  <th style={{ textAlign: "right" }}>Collection ({currencySymbol})</th>
                </tr>
              </thead>
              <tbody>
                {employeeSummary.map((row) => (
                  <tr key={row.employeeName}>
                    <td style={{ textAlign: "left" }}>{row.employeeName}</td>
                    <td style={{ textAlign: "right" }}>{row.orderCount}</td>
                    <td style={{ textAlign: "right" }}>{row.totalCollection.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <div className={classes.divider} />

        <table className={classes.table}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Category</th>
              <th style={{ textAlign: "right" }}>Amount({currencySymbol})</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(categoryTotals).map(([category, amount]) => (
              <tr key={category}>
                <td style={{ textAlign: "left" }}>{category}</td>
                <td style={{ textAlign: "right" }}>{amount.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className={classes.divider} />

        <div className={classes.summaryText}>
          <div className={classes.flexBetween}>
            <span>Total Sales</span>
            <span>{currencySymbol}{totalSales.toFixed(2)}</span>
          </div>
          <div className={classes.flexBetween}>
            <span>Total Discount</span>
            <span>{currencySymbol}{totalDiscount.toFixed(2)}</span>
          </div>
          {vatAdded > 0.004 && (
            <div className={classes.flexBetween}>
              <span>{taxLabel} (exclusive)</span>
              <span>{currencySymbol}{vatAdded.toFixed(2)}</span>
            </div>
          )}
          {Math.abs(roundOffTotal) > 0.004 && (
            <div className={classes.flexBetween}>
              <span>Round off</span>
              <span>{currencySymbol}{roundOffTotal.toFixed(2)}</span>
            </div>
          )}
        </div>

        <div className={classes.divider} />

        <div className={classes.flexBetween}>
          <span className={classes.sectionTitle}>Net Sales</span>
          <span className={classes.sectionTitle}>
            {currencySymbol}{netSales.toFixed(2)}
          </span>
        </div>
        {vatIncluded > 0.004 && (
          <div className={classes.flexBetween} style={{ fontSize: "0.85em" }}>
            <span>{taxLabel} (inclusive)</span>
            <span>{currencySymbol}{vatIncluded.toFixed(2)}</span>
          </div>
        )}

        <div className={classes.sectionTitle}>Payment Mode Summary</div>
        {Object.entries(paymentTotals).map(([mode, amount]) => (
          <div key={mode} className={classes.flexBetween}>
            <span>{mode}</span>
            <span>{currencySymbol}{amount.toFixed(2)}</span>
          </div>
        ))}

        <SalesReportSections
          totals={salesTotals}
          dayExtras={dayExtras}
          showDayExtras={reportType === "consolidated"}
          currencySymbol={currencySymbol}
          taxLabel={taxLabel}
          fontSize={12}
        />

        <div className={classes.divider} />

        <div className={classes.footer}>*** End of Report ***</div>
      </div>

      {/* Hidden printable version optimized for IMIN thermal printer */}
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
        <div
          ref={printRef}
          style={{
            width: "556px",
            padding: "20px 10px",
            lineHeight: "35px",
            backgroundColor: "white",
            border: "0px solid #FFFFFF",
          }}
        >
          <div
            style={{
              textAlign: "center",
              fontSize: "30px",
              fontWeight: "bold",
            }}
          >
            {reportTitle}
          </div>
          <div
            style={{
              textAlign: "center",
              fontSize: "28px",
              fontWeight: "bold",
            }}
          >
            {userProfile.businessName}
          </div>

          <div style={{ fontSize: "22px", textAlign: "center", width: "100%" }}>
            Date: {formatInTimezone(selectedDate, "MMM DD, YYYY")}
          </div>
          <div
            style={{
              width: "100%",
              borderBottom: "medium dotted #333333",
              height: "5px",
              marginTop: "10px",
            }}
          ></div>

          <div style={{ fontSize: "24px", marginTop: "10px" }}>
            <div>Printed: {formatInTimezone(new Date(), "MMM DD, YYYY HH:mm")}</div>
            <div>Total Orders: {filteredInvoices.length}</div>
          </div>

          {isBreakdown && employeeSummary.length > 0 && (
            <>
              <div
                style={{
                  width: "100%",
                  borderBottom: "medium dotted #333333",
                  height: "5px",
                  marginTop: "10px",
                }}
              ></div>
              <div style={{ fontSize: "28px", fontWeight: "bold", marginTop: "15px" }}>
                {breakdownLabel}-wise collection
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "24px", marginTop: "10px" }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", fontSize: "26px" }}>{breakdownLabel}</th>
                    <th style={{ textAlign: "right", fontSize: "26px" }}>Invoices</th>
                    <th style={{ textAlign: "right", fontSize: "26px" }}>Collection ({currencySymbol})</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeSummary.map((row) => (
                    <tr key={row.employeeName}>
                      <td style={{ textAlign: "left", fontSize: "24px" }}>{row.employeeName}</td>
                      <td style={{ textAlign: "right", fontSize: "24px" }}>{row.orderCount}</td>
                      <td style={{ textAlign: "right", fontSize: "24px" }}>{row.totalCollection.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <div
            style={{
              width: "100%",
              borderBottom: "medium dotted #333333",
              height: "5px",
              marginTop: "15px",
            }}
          ></div>

          <div
            style={{ fontSize: "28px", fontWeight: "bold", marginTop: "15px" }}
          >
            Category Sales
          </div>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "24px",
              marginTop: "10px",
            }}
          >
            <thead>
              <tr>
                <th style={{ textAlign: "left", fontSize: "26px" }}>
                  Category
                </th>
                <th style={{ textAlign: "right", fontSize: "26px" }}>
                  Amount({currencySymbol})
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(categoryTotals).map(([category, amount]) => (
                <tr key={category}>
                  <td style={{ textAlign: "left", fontSize: "24px" }}>
                    {category}
                  </td>
                  <td style={{ textAlign: "right", fontSize: "24px" }}>
                    {amount.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div
            style={{
              width: "100%",
              borderBottom: "medium dotted #333333",
              height: "5px",
              marginTop: "15px",
            }}
          ></div>

          <div style={{ fontSize: "26px", marginTop: "15px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "10px",
              }}
            >
              <span>Total Sales</span>
              <span>{currencySymbol}{totalSales.toFixed(2)}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "10px",
              }}
            >
              <span>Total Discount</span>
              <span>{currencySymbol}{totalDiscount.toFixed(2)}</span>
            </div>
            {vatAdded > 0.004 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "10px",
                }}
              >
                <span>{taxLabel} (exclusive)</span>
                <span>{currencySymbol}{vatAdded.toFixed(2)}</span>
              </div>
            )}
            {Math.abs(roundOffTotal) > 0.004 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "10px",
                }}
              >
                <span>Round off</span>
                <span>{currencySymbol}{roundOffTotal.toFixed(2)}</span>
              </div>
            )}
          </div>

          <div
            style={{
              width: "100%",
              borderBottom: "medium dotted #333333",
              height: "5px",
              marginTop: "10px",
            }}
          ></div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "28px",
              fontWeight: "bold",
              marginTop: "15px",
              marginBottom: "15px",
            }}
          >
            <span>Net Sales</span>
            <span>{currencySymbol}{netSales.toFixed(2)}</span>
          </div>
          {vatIncluded > 0.004 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "22px",
                marginBottom: "10px",
              }}
            >
              <span>{taxLabel} (inclusive)</span>
              <span>{currencySymbol}{vatIncluded.toFixed(2)}</span>
            </div>
          )}

          <div
            style={{
              fontSize: "28px",
              fontWeight: "bold",
              marginTop: "10px",
            }}
          >
            Payment Mode Summary
          </div>
          {Object.entries(paymentTotals).map(([mode, amount]) => (
            <div
              key={mode}
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "24px",
                marginTop: "5px",
              }}
            >
              <span>{mode}</span>
              <span>{currencySymbol}{amount.toFixed(2)}</span>
            </div>
          ))}

          <SalesReportSections
            totals={salesTotals}
            dayExtras={dayExtras}
            showDayExtras={reportType === "consolidated"}
            currencySymbol={currencySymbol}
            taxLabel={taxLabel}
            fontSize={24}
          />

          <div
            style={{
              width: "100%",
              borderBottom: "medium dotted #333333",
              height: "5px",
              marginTop: "20px",
            }}
          ></div>

          <div
            style={{
              fontSize: "26px",
              textAlign: "center",
              fontWeight: "bold",
              marginTop: "20px",
            }}
          >
            *** End of Report ***
          </div>
          <div
            style={{
              fontSize: "24px",
              paddingTop: "20px",
              width: "100%",
              textAlign: "center",
              fontStyle: "italic",
              fontWeight: "bold",
            }}
          >
            Powered By : MindTree Technologies Co. Ltd.
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ThermalSalesReportIMIN;
